import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./AttachmentAudio.module.css";
import { useTranslation } from "react-i18next";

function getTokenFromLocation() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("token");
  } catch {
    return null;
  }
}

export default function AttachmentAudio({ relativeSrc, preload = "metadata", id, label }) {
  const { t } = useTranslation();
  const baseUrl = process.env.REACT_APP_API_URL || "";
  const token = getTokenFromLocation();
  const audioRef = useRef(null);
  const [globalPlayingId, setGlobalPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const src = useMemo(() => {
    if (!relativeSrc) return "";
    const isAbsolute = /^https?:\/\//i.test(relativeSrc);
    const isRooted = relativeSrc.startsWith("/");
    let url = relativeSrc;
    if (!isAbsolute && !isRooted) {
      const base = (baseUrl || "").replace(/\/$/, "");
      url = `${base}/${relativeSrc.replace(/^\//, "")}`;
    }
    const hasQuery = url.includes("?");
    const tokenPart = token ? `${hasQuery ? "&" : "?"}token=${encodeURIComponent(token)}` : "";
    return `${url}${tokenPart}`;
  }, [baseUrl, relativeSrc, token]);

  // Ensure only one audio plays at a time across the app (pause others)
  useEffect(() => {
    function onGlobalPlay(e) {
      const playingId = e?.detail?.id;
      // Update global state for disabling UI
      setGlobalPlayingId(playingId || null);
      if (!playingId) return; // nothing is playing now
      if (playingId === (id || src)) return; // event from self
      const el = audioRef.current;
      if (el && !el.paused) {
        try { el.pause(); } catch { }
      }
    }
    window.addEventListener("chopin-audio-play", onGlobalPlay);
    return () => window.removeEventListener("chopin-audio-play", onGlobalPlay);
  }, [id, src]);

  // Do not early-return before hooks; guard rendering later

  // Native element event handlers
  const handlePlayEvent = () => {
    setIsPlaying(true);
    try {
      const detail = { id: id || src };
      window.dispatchEvent(new CustomEvent("chopin-audio-play", { detail }));
    } catch { }
  };
  const handlePauseEvent = () => {
    setIsPlaying(false);
    try {
      if (globalPlayingId === (id || src)) {
        window.dispatchEvent(new CustomEvent("chopin-audio-play", { detail: { id: null } }));
      }
    } catch { }
  };
  const handleEndedEvent = handlePauseEvent;

  const isDisabled = !!globalPlayingId && globalPlayingId !== (id || src);

  // Load metadata and time updates
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onLoaded = () => {
      setDuration(Number.isFinite(el.duration) ? el.duration : 0);
      setIsReady(true);
    };
    const onTime = () => {
      if (!seeking) setCurrentTime(el.currentTime || 0);
    };
    const onProgress = () => {
      // No-op for now; could compute buffered range if needed
    };
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("progress", onProgress);
    return () => {
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("progress", onProgress);
    };
  }, [seeking]);

  const togglePlay = async () => {
    const el = audioRef.current;
    if (!el || isDisabled) return;
    try {
      if (el.paused) {
        await el.play();
        handlePlayEvent();
      } else {
        el.pause();
        handlePauseEvent();
      }
    } catch { /* ignore */ }
  };

  const onSeekStart = () => setSeeking(true);
  const onSeek = (e) => {
    const v = Number(e.target.value || 0);
    setCurrentTime(v);
  };
  const onSeekEnd = (e) => {
    const el = audioRef.current;
    const v = Number(e.target.value || 0);
    if (el) {
      try { el.currentTime = v; } catch { }
    }
    setSeeking(false);
  };

  // Best-effort filename for download
  const suggestedName = useMemo(() => {
    try {
      const u = new URL(src, window.location.origin);
      const last = (u.pathname.split("/").pop() || "audio").split("?")[0];
      return last || "audio";
    } catch {
      const parts = String(relativeSrc).split("/");
      return parts[parts.length - 1] || "audio";
    }
  }, [src, relativeSrc]);

  if (!relativeSrc) return null;

  return (
    <div className={styles.playerWrap}>
      {label ? (
        <div className={styles.label}>
          {label}
        </div>
      ) : null}
      {/* Hidden native element */}
      <audio
        ref={audioRef}
        onPlay={handlePlayEvent}
        onPause={handlePauseEvent}
        onEnded={handleEndedEvent}
        preload={preload}
        className={styles.hiddenAudio}
      >
        <source src={src} />
        {t("audio.unsupported")}
      </audio>

      {/* Custom controls */}
      <div className={`${styles.controls} ${isDisabled ? styles.disabled : ""}`} aria-disabled={isDisabled}>
        <button
          type="button"
          className={styles.playBtn}
          onClick={togglePlay}
          disabled={isDisabled || !isReady}
          aria-label={isPlaying ? t("common.pause", { defaultValue: "Pause" }) : t("common.play", { defaultValue: "Play" })}
        >
          {isPlaying ? "❚❚" : "▶"}
        </button>

        <div className={styles.timeline}>
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={Math.min(currentTime, duration || 0)}
            onMouseDown={onSeekStart}
            onTouchStart={onSeekStart}
            onChange={onSeek}
            onMouseUp={onSeekEnd}
            onTouchEnd={onSeekEnd}
            disabled={!isReady || isDisabled}
            className={styles.seek}
            style={{
              "--_val": `${duration ? (Math.min(currentTime, duration) / duration) * 100 : 0}%`
            }}
            aria-valuemin={0}
            aria-valuemax={Math.floor(duration || 0)}
            aria-valuenow={Math.floor(currentTime || 0)}
          />
        </div>

        <a
          className={styles.downloadBtn}
          href={src}
          download={suggestedName}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("common.download", { defaultValue: "Download" })}
        >
          ⬇
        </a>
      </div>
    </div>
  );
}
